import React, { ReactNode } from 'react'
import { OverlayTrigger, Tooltip as BTooltip } from 'react-bootstrap'
import { OverlayTriggerRenderProps } from 'react-bootstrap/esm/OverlayTrigger'

export interface Props {
  label: string | ReactNode
  id: string
  className?: string
  color?: '' | 'primary'
  pos?: 'top' | 'right' | 'bottom' | 'left'
  children?:
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | ((props: OverlayTriggerRenderProps) => React.ReactNode)
}

const setRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (!ref) return
  if (typeof ref === 'function') {
    ref(value)
    return
  }
  const mutableRef = ref as React.MutableRefObject<T | null>
  mutableRef.current = value
}

const composeHandlers =
  (a?: (event: any) => void, b?: (event: any) => void) =>
  (event: any) => {
    if (a) a(event)
    if (b) b(event)
  }

const Tooltip = (mainProps: Props) => {
  const {
    label = '',
    id = '',
    pos = 'top',
    color = '',
    className = '',
    children = null,
  } = mainProps

  const classNames = `tooltip ${color ? `tooltip--color-${color}` : ''} ${className}`

  const renderTooltip = (props: any) => (
    <BTooltip id={id} className={classNames} {...props}>
      {label}
    </BTooltip>
  )

  if (!children) {
    return null
  }

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement={pos}
      delay={{ show: 50, hide: 50 }}
      transition={false}
      overlay={renderTooltip}
    >
      {(triggerProps: OverlayTriggerRenderProps) => {
        if (typeof children === 'function') {
          return children(triggerProps)
        }

        if (!React.isValidElement(children)) {
          return children
        }

        const childProps = (children.props || {}) as Record<string, any>
        const mergedProps = {
          ...childProps,
          ...triggerProps,
          ref: (node: HTMLElement | null) => {
            setRef((children as any).ref, node)
            setRef(triggerProps.ref as React.Ref<HTMLElement>, node)
          },
          onBlur: composeHandlers(childProps.onBlur, triggerProps.onBlur),
          onClick: composeHandlers(childProps.onClick, triggerProps.onClick),
          onFocus: composeHandlers(childProps.onFocus, triggerProps.onFocus),
          onMouseOut: composeHandlers(childProps.onMouseOut, triggerProps.onMouseOut),
          onMouseOver: composeHandlers(childProps.onMouseOver, triggerProps.onMouseOver),
        }

        return React.cloneElement(children, mergedProps)
      }}
    </OverlayTrigger>
  )
}

export default Tooltip
